/**
 * Is this account actually ready to call anyone?
 *
 * Arun on the old Configuration screen: "the configuration for each account is a
 * little daunting and hard to know what to do." Six tabs, forty controls, and no
 * indication of which of them were load-bearing or which were already done. So this
 * sits above the tabs and answers the two questions the page is opened with — am I
 * finished, and if not what is next — then points at the tab that fixes it.
 *
 * Every check is driven by real state, never by "a row exists". That distinction is
 * the whole point: a credential row proves someone typed something, not that 8loop
 * accepts it, and this repo's own dev account spent weeks showing a confident "Email
 * and password" over a password 8loop had been answering 401 to the entire time. So
 * "never verified" is its own state, visibly different from "working".
 *
 * The caveats at the bottom are separate from the checks on purpose. Dry-run mode and
 * a paused account are not things to tick off — they are reasons a perfectly
 * configured account still rings nobody, which is the other way this screen used to
 * mislead.
 */

import { useState } from "react";
import { AlertTriangle, Check, ChevronDown, ChevronRight, Circle, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { relativeTime } from "@/lib/labels";
import type { AccountConfig, CallbackAgentsResponse } from "@/lib/api";

type Status = "ok" | "warn" | "todo";

type CheckItem = {
  id: string;
  label: string;
  status: Status;
  detail: string;
  /** The tab that fixes it. Omitted when there is nothing to fix. */
  tab?: string;
  action?: string;
};

export function SetupChecklist({
  config,
  agents,
  onGoToTab,
}: {
  config: AccountConfig;
  /**
   * The account's callback list, when it has loaded. Undefined means "not known yet", and the
   * agent check is left out rather than guessed — an account with agents set up must never be
   * told for half a second that it has none.
   */
  agents?: CallbackAgentsResponse;
  onGoToTab: (tab: string) => void;
}) {
  const checks = buildChecks(config, agents);
  const outstanding = checks.filter((c) => c.status !== "ok");
  const ready = outstanding.length === 0;
  // Expanded when there is work to do, collapsed when there is not. A green checklist
  // that stays open forever is just a taller header.
  const [expanded, setExpanded] = useState(!ready);

  const caveats = buildCaveats(config);

  return (
    <section className="mb-6 max-w-3xl overflow-hidden rounded-lg border bg-card">
      <button
        type="button"
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/40"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">
            {ready ? "This account is set up" : headline(outstanding)}
          </p>
          <p className="text-xs text-muted-foreground">
            {ready
              ? `All ${checks.length} checks pass.`
              : `${outstanding.length} of ${checks.length} checks need attention.`}
          </p>
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">
          {expanded ? "Hide" : "Show"} checks
        </span>
      </button>

      {expanded && (
        <ul className="divide-y border-t">
          {checks.map((c) => (
            <li key={c.id} className="flex items-start gap-3 px-4 py-3">
              <StatusIcon status={c.status} />
              <div className="min-w-0 flex-1">
                <p className={cn("text-sm", c.status === "ok" && "text-muted-foreground")}>
                  {c.label}
                </p>
                <p className="text-xs text-muted-foreground">{c.detail}</p>
              </div>
              {c.tab && c.status !== "ok" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() => onGoToTab(c.tab!)}
                >
                  {c.action ?? "Fix"}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {caveats.length > 0 && (
        <ul className="space-y-1 border-t bg-muted/40 px-4 py-3">
          {caveats.map((c) => (
            <li key={c.id} className="flex items-start gap-2 text-xs text-muted-foreground">
              <Info className="mt-0.5 size-3.5 shrink-0" />
              <span>
                <span className="font-medium text-foreground">{c.label}</span> {c.detail}
                {c.link && (
                  <>
                    {" "}
                    {/* Underlined grey-on-grey at 12px, with no hover response, reads
                        as emphasis rather than a link. The colour change on hover is
                        what identifies it as one. */}
                    <Link
                      className="underline underline-offset-2 hover:text-foreground"
                      to={c.link.to}
                    >
                      {c.link.label}
                    </Link>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function StatusIcon({ status }: { status: Status }) {
  if (status === "ok") return <Check className="mt-0.5 size-4 shrink-0 text-success" />;
  if (status === "warn") return <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" />;
  return <Circle className="mt-0.5 size-4 shrink-0 text-destructive" />;
}

/**
 * Names the most urgent thing rather than counting, so the headline is actionable.
 *
 * The label is used verbatim: some are imperatives ("Connect this account to 8loop")
 * and some are statements of fact ("8loop rejected this credential"), and no single
 * prefix reads correctly in front of both — "Next: 8loop rejected this credential"
 * was the first attempt. The count underneath supplies the ordering instead.
 */
function headline(outstanding: CheckItem[]): string {
  const blocking = outstanding.find((c) => c.status === "todo") ?? outstanding[0]!;
  return blocking.label.replace(/\.$/, "");
}

function buildChecks(config: AccountConfig, agents?: CallbackAgentsResponse): CheckItem[] {
  const { credentials: cred, account } = config;
  const totalCallbacks = Object.values(config.queue.byStatus).reduce((n, v) => n + v, 0);
  const overrides = config.routes.filter((r) => r.enabled);
  // `callback_no_time` is a branch of `callback_requested`, not a call ending of its own, so
  // counting it here would inflate "5 enabled" to six and list the same situation twice.
  const enabledOutcomes = config.outcome_rules.filter(
    (r) => r.enabled && r.outcome !== "callback_no_time",
  );

  const items: (CheckItem | null)[] = [
    credentialCheck(cred),
    // The direction nobody thinks about until it is silent: 8loop has to be able to
    // reach US, and that is a post-call hook on each agent — written by the FDE's own Claude
    // Code from the brief this app hands over, not pasted from here.
    !cred.has_ingest_token
      ? {
          id: "ingest",
          label: "Let 8loop send us callbacks",
          status: "todo" as Status,
          detail:
            "No ingest token, so there is no URL for 8loop to post to. Generate one on Setup.",
          tab: "setup",
          action: "Set up",
        }
      : totalCallbacks === 0
        ? {
            id: "ingest",
            label: "Let 8loop send us callbacks",
            status: "warn" as Status,
            detail:
              "The URL and token are ready, but no callback has ever arrived. The post-call hook " +
              "is per agent — add the agent you are working on, then hand its brief to your " +
              "Claude Code.",
            tab: "agents",
            action: "Go to agents",
          }
        : {
            id: "ingest",
            label: "8loop is sending us callbacks",
            status: "ok" as Status,
            detail: `${totalCallbacks} received so far.`,
          },
    config.calling_windows.length === 0
      ? {
          id: "hours",
          label: "Say when this account may call",
          status: "todo" as Status,
          detail:
            "No calling window is set, so every callback is scheduled and then never dialled.",
          tab: "hours",
          action: "Set hours",
        }
      : {
          id: "hours",
          label: "Calling hours are set",
          status: "ok" as Status,
          detail: `${config.calling_windows.length} window${
            config.calling_windows.length === 1 ? "" : "s"
          } across the week, in ${account.timezone}.`,
        },
    // Routing needs no rules — a callback dials back on the channel it came from. What
    // it does need is a channel list, because the code action sends a channel *id* and
    // only the cached list turns that into something dialable. So the check is "has
    // 8loop ever been read?", not "is there a rule?". It also gates the Add-an-agent
    // dialog, whose candidates come out of the same cache.
    account.channels_refreshed_at === null
      ? {
          id: "routing",
          label: "Read this tenant's agents and channels once",
          status: "todo" as Status,
          detail:
            "The channel list has never been fetched from 8loop, so there are no agents to " +
            "choose from and a callback's channel id cannot be turned into a number to dial " +
            "from. Read them on Agents.",
          tab: "agents",
          action: "Read them",
        }
      : account.channels_refresh_error
        ? {
            id: "routing",
            label: "8loop rejected the last channel refresh",
            status: "warn" as Status,
            detail:
              `The list in use is whatever was read before that, so a channel created in ` +
              `8loop since is missing and its callbacks will need attention. 8loop said: ` +
              `${account.channels_refresh_error}`,
            tab: "agents",
            action: "Review",
          }
        : {
            id: "routing",
            label: "Every channel calls back on itself",
            status: "ok" as Status,
            detail:
              overrides.length === 0
                ? "No overrides needed. Each agent's page shows its own channels, and Agents has the full list."
                : `${overrides.length} channel${overrides.length === 1 ? "" : "s"} ${
                    overrides.length === 1 ? "is" : "are"
                  } overridden to dial out elsewhere; the rest call back on themselves.`,
          },
    agentCheck(agents),
    enabledOutcomes.length === 0
      ? {
          id: "outcomes",
          label: "Choose which call endings deserve a callback",
          status: "todo" as Status,
          detail: "Nothing is enabled, so every callback 8loop sends is recorded and ignored.",
          tab: "outcomes",
          action: "Choose",
        }
      : {
          id: "outcomes",
          label: "Call endings that deserve a callback are chosen",
          status: "ok" as Status,
          detail: `${enabledOutcomes.length} enabled: ${enabledOutcomes
            .map((r) => r.label.toLowerCase())
            .join(", ")}.`,
        },
  ];

  return items.filter((c): c is CheckItem => c !== null);
}

/**
 * Is any agent actually set up to send callbacks?
 *
 * The check every version of this screen was missing, and the reason Arun could not tell which
 * agents were being worked on: the account could pass everything else while no agent on the
 * tenant had the hook installed, which means an empty queue forever.
 *
 * `present === null` is deliberately not counted as installed *or* as missing. An agent nobody has
 * checked against 8loop is unknown, and saying "not installed" would send someone to reinstall a
 * hook that may already be live.
 */
function agentCheck(agents?: CallbackAgentsResponse): CheckItem | null {
  if (!agents) return null;

  const list = agents.agents;
  if (list.length === 0) {
    return {
      id: "agents",
      label: "Say which agents do callbacks",
      status: "todo",
      detail:
        agents.unlisted.length > 0
          ? `No agent is on the list, though ${agents.unlisted.length} ${
              agents.unlisted.length === 1 ? "is" : "are"
            } already sending callbacks. Add them so their setup is somewhere you can see it.`
          : "No agent is set up, so nothing on this tenant is sending finished calls to this app.",
      tab: "agents",
      action: "Add one",
    };
  }

  const installed = list.filter((a) => a.hook.present === true);
  const missing = list.filter((a) => a.hook.present === false);
  const unknown = list.filter((a) => a.hook.present === null);

  if (installed.length === 0) {
    return {
      id: "agents",
      label:
        missing.length > 0
          ? "Install the post-call hook on an agent"
          : "Check whether the post-call hook is installed",
      status: "todo",
      detail:
        missing.length > 0
          ? `${list.length} agent${list.length === 1 ? " is" : "s are"} on the list and none has ` +
            "the hook installed, so no finished call reaches this app. Open one and hand its " +
            "brief to your Claude Code."
          : `${unknown.length} agent${
              unknown.length === 1 ? " has" : "s have"
            } never been checked against 8loop, so whether anything is installed is unknown. ` +
            "Open one to find out.",
      tab: "agents",
      action: "Open",
    };
  }

  if (missing.length > 0 || unknown.length > 0) {
    return {
      id: "agents",
      label: `${installed.length} of ${list.length} agents have the post-call hook`,
      status: "warn",
      detail:
        [
          missing.length > 0
            ? `${missing.length} still ${missing.length === 1 ? "has" : "have"} no hook (${missing
                .slice(0, 2)
                .map((a) => a.agent_name ?? a.agent_id)
                .join(", ")}${missing.length > 2 ? ", …" : ""})`
            : null,
          unknown.length > 0 ? `${unknown.length} not checked against 8loop yet` : null,
        ]
          .filter(Boolean)
          .join("; ") + ". Callbacks only arrive from agents that have it.",
      tab: "agents",
      action: "Review",
    };
  }

  return {
    id: "agents",
    label: `The post-call hook is installed on ${
      list.length === 1 ? "the one agent on the list" : `all ${list.length} agents on the list`
    }`,
    status: "ok",
    detail: `${list
      .slice(0, 3)
      .map((a) => a.agent_name ?? a.agent_id)
      .join(", ")}${list.length > 3 ? `, and ${list.length - 3} more` : ""}.`,
  };
}

function credentialCheck(cred: AccountConfig["credentials"]): CheckItem {
  if (cred.mode === "none") {
    return {
      id: "credential",
      label: "Connect this account to 8loop",
      status: "todo",
      detail: "No API key is stored, so nothing can be read from 8loop and nothing can be dialled.",
      tab: "setup",
      action: "Add a key",
    };
  }
  if (cred.credential_error) {
    return {
      id: "credential",
      label: "The stored credential can't be read",
      status: "todo",
      detail: cred.credential_error,
      tab: "setup",
      action: "Re-enter",
    };
  }
  if (cred.last_check_at === null) {
    return {
      id: "credential",
      label: "Check that the 8loop credential works",
      status: "warn",
      detail:
        "Something is stored but nobody has ever asked 8loop whether it is accepted. " +
        "Test connection only reads the agent list — it places no call.",
      tab: "setup",
      action: "Test it",
    };
  }
  if (cred.last_check_ok === false) {
    return {
      id: "credential",
      label: "8loop rejected this credential",
      status: "todo",
      detail: `Checked ${relativeTime(cred.last_check_at)}: ${
        cred.last_check_error ?? "no reason given"
      }`,
      tab: "setup",
      action: "Fix it",
    };
  }
  return {
    id: "credential",
    label: "Connected to 8loop",
    status: "ok",
    detail:
      `API key ${cred.api_key_masked ?? ""} verified ${relativeTime(cred.last_check_at)}. ` +
      "Retries are safe — 8loop honours our idempotency key.",
  };
}

type Caveat = {
  id: string;
  label: string;
  detail: string;
  link?: { to: string; label: string };
};

/** Reasons a fully configured account still rings nobody. Not checklist items. */
function buildCaveats(config: AccountConfig): Caveat[] {
  const out: Caveat[] = [];
  if (config.dry_run) {
    out.push({
      id: "dry-run",
      label: "Dry run.",
      detail:
        "This server simulates every dial and nothing rings. That is a deployment setting " +
        `(DIAL_MODE is "${config.dial_mode}"), not something on this page.`,
    });
  }
  if (config.account.paused) {
    out.push({
      id: "paused",
      label: "Callbacks are paused for this account.",
      detail: "Nothing will be dialled until it is resumed.",
      link: { to: `/accounts/${config.account.id}/queue`, label: "Resume on the queue" },
    });
  }
  if (config.account.daily_dial_cap !== null) {
    out.push({
      id: "cap",
      label: `Capped at ${config.account.daily_dial_cap} dials a day.`,
      detail: "Anything over the cap waits for tomorrow.",
    });
  }
  return out;
}
